import { useEffect } from "react";
import dayjs, { ISO_DATE_FORMAT } from "../dayjs";
import { endOfCurrentYear, startOfPreviousMonth } from "../misc/dateUtils";
import { useQuery } from "../misc/useQuery";
import type { OrdersSearchCriteria } from "./topBar/useOrderSearch";

function getOrdersSearchPatchFromQueryParams(
  searchParams: URLSearchParams,
  fallbackAgentId: string | null,
): Partial<OrdersSearchCriteria> | null {
  const query = searchParams.get("query") ?? "";
  const dateFromQuery = searchParams.get("dateFrom");
  const dateToQuery = searchParams.get("dateTo");
  const startDate = dateFromQuery
    ? dayjs(dateFromQuery, ISO_DATE_FORMAT, true).isValid()
      ? dayjs(dateFromQuery, ISO_DATE_FORMAT)
      : startOfPreviousMonth()
    : startOfPreviousMonth();
  const endDate = dateToQuery
    ? dayjs(dateToQuery, ISO_DATE_FORMAT, true).isValid()
      ? dayjs(dateToQuery, ISO_DATE_FORMAT)
      : endOfCurrentYear()
    : endOfCurrentYear();
  const agentId = searchParams.get("agentId");
  const customerId = searchParams.get("customerId") ?? null;
  if (!query && !dateFromQuery && !dateToQuery && !agentId && !customerId) {
    return null;
  }
  return {
    text: query,
    startDate,
    endDate,
    agentId: agentId ?? fallbackAgentId,
    statuses: [],
    customerId,
  };
}

interface Options {
  readonly fallbackAgentId: string | null;
  readonly onApply: (patch: Partial<OrdersSearchCriteria>) => void;
}

/**
 * Reads the current location query string (via {@link useQuery}) and applies search criteria once on mount
 * (e.g. shared links with filters).
 */
export function useOrdersSearchFromQueryParams({ fallbackAgentId, onApply }: Options): void {
  const searchParams = useQuery();
  useEffect(() => {
    const patch = getOrdersSearchPatchFromQueryParams(searchParams, fallbackAgentId);
    if (patch) {
      onApply(patch);
    }
  }, []);
}
